<template>
    <div class="container-fluid default-dashboard py-3">
        <!-- Top Stats / Performance Cards -->
        <div class="row mb-3 g-3">
            <div class="col-xl-3 col-md-6 col-sm-6">
                <div class="card stat-card shadow-sm border-0 h-100">
                    <div class="card-body d-flex align-items-center">
                        <div class="stat-icon bg-primary-subtle text-primary rounded-3 me-3">
                            <i class="fa fa-comments fa-2x"></i>
                        </div>
                        <div class="flex-grow-1">
                            <span class="text-muted fw-semibold text-uppercase small">Total Interaksi Masuk</span>
                            <h3 class="mb-0 fw-bold text-dark">{{ metrics.total }}</h3>
                            <small class="text-muted">Periode: {{ activePeriodLabel }}</small>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-xl-2 col-md-6 col-sm-6">
                <div class="card stat-card shadow-sm border-0 h-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="text-muted fw-semibold text-uppercase small">Merespon</span>
                            <span class="badge bg-info-subtle text-info fw-bold">{{ metrics.responseRate }}%</span>
                        </div>
                        <h3 class="mb-1 fw-bold text-info">{{ metrics.merespon }}</h3>
                        <div class="progress" style="height: 5px;">
                            <div class="progress-bar bg-info" :style="{ width: `${metrics.responseRate}%` }"></div>
                        </div>
                        <small class="text-muted mt-1 d-block">CS merespon chat/kontak</small>
                    </div>
                </div>
            </div>

            <div class="col-xl-2 col-md-6 col-sm-6">
                <div class="card stat-card shadow-sm border-0 h-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="text-muted fw-semibold text-uppercase small">Tdk Merespon</span>
                            <span class="badge bg-warning-subtle text-warning fw-bold">{{ metrics.noResponseRate }}%</span>
                        </div>
                        <h3 class="mb-1 fw-bold text-warning">{{ metrics.tidakMerespon }}</h3>
                        <div class="progress" style="height: 5px;">
                            <div class="progress-bar bg-warning" :style="{ width: `${metrics.noResponseRate}%` }"></div>
                        </div>
                        <small class="text-muted mt-1 d-block">Belum/tidak ada respon</small>
                    </div>
                </div>
            </div>

            <div class="col-xl-3 col-md-6 col-sm-6">
                <div class="card stat-card shadow-sm border-success border-2 h-100 bg-success-subtle bg-opacity-25">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="text-success fw-bold text-uppercase small">
                                <i class="fa fa-trophy me-1"></i> Berhasil Closing
                            </span>
                            <span class="badge bg-success text-white fw-bold px-2 py-1">Rate: {{ metrics.closingRate }}%</span>
                        </div>
                        <div class="d-flex align-items-baseline">
                            <h2 class="mb-0 fw-bolder text-success">{{ metrics.closing }}</h2>
                            <span class="text-muted ms-2 small">dari {{ metrics.total }} leads</span>
                        </div>
                        <div class="progress mt-2" style="height: 6px;">
                            <div class="progress-bar bg-success" :style="{ width: `${metrics.closingRate}%` }"></div>
                        </div>
                        <small class="text-success fw-semibold mt-1 d-block">Target konversi order sukses</small>
                    </div>
                </div>
            </div>

            <div class="col-xl-2 col-md-6 col-sm-6">
                <div class="card stat-card shadow-sm border-0 h-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-1">
                            <span class="text-muted fw-semibold text-uppercase small">Reject & Cancel</span>
                            <span class="badge bg-danger-subtle text-danger fw-bold">{{ metrics.dropRate }}%</span>
                        </div>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <span class="d-block small text-danger fw-bold">Reject: {{ metrics.reject }}</span>
                                <span class="d-block small text-secondary fw-bold">Cancel: {{ metrics.cancel }}</span>
                            </div>
                            <h3 class="mb-0 fw-bold text-danger">{{ metrics.reject + metrics.cancel }}</h3>
                        </div>
                        <small class="text-muted mt-1 d-block">Gagal konversi order</small>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sales / CS Performance Breakdown Card -->
        <div class="card shadow-sm border-0 mb-3" v-if="salesPerformanceList.length > 0">
            <div class="card-header bg-white border-bottom py-2 d-flex justify-content-between align-items-center cursor-pointer" @click="showSalesRank = !showSalesRank">
                <div class="d-flex align-items-center">
                    <i class="fa fa-users text-primary me-2"></i>
                    <h6 class="mb-0 fw-bold text-dark">Performa & Kemampuan Sales / CS (Periode: {{ activePeriodLabel }})</h6>
                    <span class="badge bg-primary ms-2">{{ salesPerformanceList.length }} CS Aktif</span>
                </div>
                <button class="btn btn-sm btn-outline-secondary py-0">
                    <i :class="showSalesRank ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                </button>
            </div>
            <div class="card-body p-0" v-show="showSalesRank">
                <div class="table-responsive">
                    <table class="table table-hover table-sm align-middle mb-0">
                        <thead class="table-light">
                            <tr>
                                <th class="ps-3" style="width: 40px;">#</th>
                                <th>Nama CS / Sales</th>
                                <th class="text-center">Total Handle</th>
                                <th class="text-center">Merespon</th>
                                <th class="text-center">Tdk Respon</th>
                                <th class="text-center">Closing 🎯</th>
                                <th class="text-center">Closing Rate</th>
                                <th class="text-center">Reject</th>
                                <th class="text-center">Cancel</th>
                                <th class="text-end pe-3">Status Kemampuan</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(sales, idx) in salesPerformanceList" :key="sales.salesName">
                                <td class="ps-3 fw-bold text-muted">{{ idx + 1 }}</td>
                                <td>
                                    <div class="d-flex align-items-center">
                                        <div class="avatar-circle me-2 bg-primary-subtle text-primary fw-bold">
                                            {{ (sales.salesName || 'CS')[0].toUpperCase() }}
                                        </div>
                                        <div>
                                            <span class="fw-bold d-block text-dark">{{ sales.salesName || 'Tidak Diketahui' }}</span>
                                            <small class="text-muted">{{ sales.salesEmail || '-' }}</small>
                                        </div>
                                    </div>
                                </td>
                                <td class="text-center fw-bold">{{ sales.total }}</td>
                                <td class="text-center text-info fw-semibold">{{ sales.merespon }}</td>
                                <td class="text-center text-warning fw-semibold">{{ sales.tidakMerespon }}</td>
                                <td class="text-center text-success fw-bold">{{ sales.closing }}</td>
                                <td class="text-center">
                                    <div class="d-flex align-items-center justify-content-center">
                                        <div class="progress flex-grow-1 me-2" style="height: 6px; width: 60px;">
                                            <div class="progress-bar bg-success" :style="{ width: `${sales.closingRate}%` }"></div>
                                        </div>
                                        <span class="fw-bold text-success">{{ sales.closingRate }}%</span>
                                    </div>
                                </td>
                                <td class="text-center text-danger">{{ sales.reject }}</td>
                                <td class="text-center text-secondary">{{ sales.cancel }}</td>
                                <td class="text-end pe-3">
                                    <span v-if="sales.closingRate >= 40" class="badge bg-success">
                                        <i class="fa fa-star me-1"></i> Top Performer
                                    </span>
                                    <span v-else-if="sales.closingRate >= 20" class="badge bg-primary">
                                        <i class="fa fa-check me-1"></i> Baik
                                    </span>
                                    <span v-else class="badge bg-warning text-dark">
                                        <i class="fa fa-bolt me-1"></i> Perlu Dorongan
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Main Card: Controls & Customer Interaction List -->
        <div class="card shadow-sm border-0">
            <div class="card-header bg-white border-bottom py-3">
                <div class="row g-2 align-items-center justify-content-between">
                    <div class="col-lg-5 col-md-12">
                        <div class="d-flex align-items-center flex-wrap gap-2">
                            <h5 class="mb-0 fw-bold text-dark">Order Interaction</h5>
                            <span class="badge bg-light text-dark border">Total: {{ total }}</span>
                            <div class="btn-group btn-group-sm" role="group">
                                <button type="button" class="btn" :class="activePeriod === 'today' ? 'btn-primary' : 'btn-outline-secondary'" @click="setPeriodPreset('today')">Hari Ini</button>
                                <button type="button" class="btn" :class="activePeriod === 'yesterday' ? 'btn-primary' : 'btn-outline-secondary'" @click="setPeriodPreset('yesterday')">Kemarin</button>
                                <button type="button" class="btn" :class="activePeriod === 'week' ? 'btn-primary' : 'btn-outline-secondary'" @click="setPeriodPreset('week')">7 Hari</button>
                                <button type="button" class="btn" :class="activePeriod === 'month' ? 'btn-primary' : 'btn-outline-secondary'" @click="setPeriodPreset('month')">Bulan Ini</button>
                                <button type="button" class="btn" :class="activePeriod === 'all' ? 'btn-primary' : 'btn-outline-secondary'" @click="setPeriodPreset('all')">Semua</button>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-7 col-md-12 text-lg-end">
                        <div class="d-flex align-items-center justify-content-lg-end flex-wrap gap-2">
                            <!-- WhatsApp Report Button -->
                            <button class="btn btn-sm btn-success shadow-sm" @click="openWhatsAppModal">
                                <i class="fab fa-whatsapp me-1"></i> Laporan WA
                            </button>

                            <!-- Download Excel Button -->
                            <button class="btn btn-sm btn-outline-success shadow-sm" :disabled="downloadingExcel" @click="downloadExcelReport">
                                <i class="fa fa-file-excel-o me-1"></i>
                                <span v-if="downloadingExcel">Downloading...</span>
                                <span v-else>Download Excel</span>
                            </button>

                            <!-- Add New Interaction Button -->
                            <button class="btn btn-sm btn-primary shadow-sm" @click="openModal()">
                                <i class="fa fa-plus me-1"></i> Input Interaksi Masuk
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Custom Filter Bar -->
                <div class="row g-2 mt-2 pt-2 border-top align-items-end">
                    <div class="col-xl-2 col-md-3 col-sm-6">
                        <label class="form-label small mb-1 fw-semibold text-muted">Tanggal Mulai</label>
                        <input type="date" v-model="filterStartDate" class="form-control form-control-sm" @change="applyDateFilter">
                    </div>
                    <div class="col-xl-2 col-md-3 col-sm-6">
                        <label class="form-label small mb-1 fw-semibold text-muted">Tanggal Selesai</label>
                        <input type="date" v-model="filterEndDate" class="form-control form-control-sm" @change="applyDateFilter">
                    </div>
                    <div class="col-xl-2 col-md-3 col-sm-6">
                        <label class="form-label small mb-1 fw-semibold text-muted">Filter Status</label>
                        <select v-model="filterStatus" class="form-select form-select-sm" @change="applyStatusFilter">
                            <option value="">Semua Status (5 Status)</option>
                            <option value="Merespon">💬 Merespon</option>
                            <option value="Tidak Merespon">⏳ Tidak Merespon</option>
                            <option value="Closing">🎯 Closing</option>
                            <option value="Reject">❌ Reject</option>
                            <option value="Cancel">🚫 Cancel</option>
                        </select>
                    </div>
                    <div class="col-xl-2 col-md-3 col-sm-6">
                        <label class="form-label small mb-1 fw-semibold text-muted">Divisi</label>
                        <select v-model="filterDivisi" class="form-select form-select-sm" @change="handleFilterDivisiChange">
                            <option :value="null">Semua Divisi</option>
                            <option v-for="opt in divisiOptions" :key="opt.ID" :value="opt.ID">{{ opt.name }}</option>
                        </select>
                    </div>
                    <div class="col-xl-2 col-md-3 col-sm-6" v-if="filterDivisiTeamOptions.length > 0">
                        <label class="form-label small mb-1 fw-semibold text-muted">Tim Divisi</label>
                        <select v-model="filterDivisiTeam" class="form-select form-select-sm" @change="applyFilter">
                            <option :value="null">Semua Tim</option>
                            <option v-for="opt in filterDivisiTeamOptions" :key="opt.ID" :value="opt.ID">{{ opt.name }}</option>
                        </select>
                    </div>
                    <div class="col-xl-2 col-md-3 col-sm-6">
                        <button class="btn btn-sm btn-outline-secondary w-100" @click="resetFilters">
                            <i class="fa fa-refresh me-1"></i> Reset Filter
                        </button>
                    </div>
                </div>
            </div>

            <div class="card-body p-0">
                <DataTable
                    :headers="headers"
                    :items="customers"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <!-- Status Column with Quick Update Dropdown -->
                    <template #item.status="{ item }">
                        <div class="dropdown d-inline-block">
                            <button
                                class="btn btn-sm dropdown-toggle py-1 px-2 border-0 fw-semibold"
                                :class="getStatusBadgeClass(item.status)"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <i :class="getStatusIcon(item.status)" class="me-1"></i>
                                {{ item.status || 'Merespon' }}
                            </button>
                            <ul class="dropdown-menu dropdown-menu-sm shadow">
                                <li class="dropdown-header text-uppercase small text-muted">Ubah Status Interaksi</li>
                                <li>
                                    <a class="dropdown-item small text-info" href="#" @click.prevent="quickChangeStatus(item, 'Merespon')">
                                        <i class="fa fa-commenting me-2"></i> Merespon
                                    </a>
                                </li>
                                <li>
                                    <a class="dropdown-item small text-warning" href="#" @click.prevent="quickChangeStatus(item, 'Tidak Merespon')">
                                        <i class="fa fa-hourglass-half me-2"></i> Tidak Merespon
                                    </a>
                                </li>
                                <li><hr class="dropdown-divider my-1"></li>
                                <li class="dropdown-header text-uppercase small text-muted">Masuk ke Order</li>
                                <li>
                                    <a class="dropdown-item small text-success fw-bold" href="#" @click.prevent="quickChangeStatus(item, 'Closing')">
                                        <i class="fa fa-check-circle me-2"></i> Closing 🎯
                                    </a>
                                </li>
                                <li>
                                    <a class="dropdown-item small text-danger" href="#" @click.prevent="quickChangeStatus(item, 'Reject')">
                                        <i class="fa fa-times-circle me-2"></i> Reject
                                    </a>
                                </li>
                                <li>
                                    <a class="dropdown-item small text-secondary" href="#" @click.prevent="quickChangeStatus(item, 'Cancel')">
                                        <i class="fa fa-ban me-2"></i> Cancel
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </template>

                    <!-- Customer Phone with 1-Click WhatsApp Chat -->
                    <template #item.no_telp="{ item }">
                        <div class="d-flex align-items-center">
                            <span>{{ item.no_telp || '-' }}</span>
                            <a
                                v-if="item.no_telp"
                                :href="getWaChatUrl(item.no_telp, item.name)"
                                target="_blank"
                                class="btn btn-xs btn-outline-success ms-2 p-1 py-0 rounded-circle"
                                title="Chat Customer via WhatsApp"
                            >
                                <i class="fab fa-whatsapp"></i>
                            </a>
                        </div>
                    </template>

                    <!-- Interaction Date / Created At -->
                    <template #item.interaction_date="{ item }">
                        <div>
                            <span class="d-block fw-semibold text-dark">{{ formatDate(item.interaction_date || item.CreatedAt) }}</span>
                            <small class="text-muted">{{ formatTime(item.interaction_date || item.CreatedAt) }}</small>
                        </div>
                    </template>

                    <!-- Customer Info (Code & Name) -->
                    <template #item.name="{ item }">
                        <div>
                            <span class="fw-bold text-dark d-block">{{ item.name }}</span>
                            <small class="text-muted font-monospace">{{ item.code }}</small>
                        </div>
                    </template>

                    <!-- Sales / CS Name -->
                    <template #item.sales_name="{ item }">
                        <div>
                            <span class="fw-semibold text-dark d-block">{{ item.sales_name || item.user_email || '-' }}</span>
                            <small v-if="item.sales_name && item.user_email" class="text-muted">{{ item.user_email }}</small>
                        </div>
                    </template>

                    <!-- Divisi & Tim -->
                    <template #item.divisi="{ item }">
                        <div>
                            <span class="fw-semibold text-dark d-block">{{ item.divisi?.name || '-' }}</span>
                            <small class="text-muted">{{ item.divisi_team?.name || '' }}</small>
                        </div>
                    </template>

                    <!-- Action Buttons -->
                    <template #item.actions="{ item }">
                        <div class="btn-group btn-group-sm" role="group">
                            <button
                                v-if="item.status === 'Closing'"
                                class="btn btn-sm btn-outline-success"
                                @click="goToPOS(item)"
                                title="Buat Transaksi POS"
                            >
                                <i class="fa fa-shopping-cart"></i>
                            </button>
                            <button class="btn btn-sm btn-outline-info" @click="viewDetail(item)" title="Lihat Detail">
                                <i class="fa fa-eye"></i>
                            </button>
                            <button class="btn btn-sm btn-outline-warning" @click="editCustomer(item)" title="Edit Interaksi">
                                <i class="fa fa-edit"></i>
                            </button>
                            <button class="btn btn-sm btn-outline-danger" @click="deleteCustomer(item)" title="Hapus">
                                <i class="fa fa-trash"></i>
                            </button>
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <!-- Modal Input / Edit Interaction -->
    <div class="modal fade" id="orderInteractionModal" tabindex="-1" ref="modalRef" aria-labelledby="orderInteractionModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content shadow-lg border-0">
                <div class="modal-header bg-light border-0">
                    <h5 class="modal-title fw-bold text-dark">
                        <i class="fa fa-comments text-primary me-2"></i>{{ modalTitle }}
                    </h5>
                    <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
                </div>
                <div class="modal-body p-4">
                    <!-- Status & Interaction Overview Section -->
                    <div class="card bg-light border-0 p-3 mb-3">
                        <div class="row g-3">
                            <div class="col-md-6">
                                <label class="form-label fw-bold">Status Interaksi <span class="text-danger">*</span></label>
                                <select v-model="form.status" class="form-select" required>
                                    <optgroup label="Baru Masuk Interaksi">
                                        <option value="Merespon">💬 Merespon</option>
                                        <option value="Tidak Merespon">⏳ Tidak Merespon</option>
                                    </optgroup>
                                    <optgroup label="Masuk ke Order">
                                        <option value="Closing">🎯 Closing (Berhasil Deal)</option>
                                        <option value="Reject">❌ Reject (Menolak)</option>
                                        <option value="Cancel">🚫 Cancel (Dibatalkan)</option>
                                    </optgroup>
                                </select>
                                <small class="text-muted d-block mt-1">Pilih status respon calon customer saat ini</small>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label fw-bold">Tanggal & Jam Interaksi</label>
                                <input v-model="form.interaction_date_str" type="datetime-local" class="form-control" />
                                <small class="text-muted d-block mt-1">Waktu interaksi/chat masuk dari customer</small>
                            </div>
                        </div>
                    </div>

                    <!-- Divisi & CS Assignment -->
                    <div class="row g-3 mb-3">
                        <div class="col-md-6">
                            <label class="form-label fw-bold">Divisi <span class="text-danger">*</span></label>
                            <select v-if="!form.code" v-model="form.divisi" class="form-select" required>
                                <option :value="null" disabled>Pilih Divisi</option>
                                <option v-for="option in divisiOptions" :key="option.ID" :value="option">
                                    {{ option.name }}
                                </option>
                            </select>
                            <input v-else :value="form.divisi?.name || form.divisi" :disabled="true" type="text" class="form-control bg-light" />
                        </div>
                        <div class="col-md-6">
                            <label class="form-label fw-bold">Divisi Team</label>
                            <select v-if="!form.code" v-model="form.divisi_team" class="form-select">
                                <option :value="null">Pilih Tim Divisi</option>
                                <option v-for="option in divisiTeamOptions" :key="option.ID" :value="option">
                                    {{ option.name }}
                                </option>
                            </select>
                            <input v-else :value="form.divisi_team?.name || form.divisi_team || '-'" :disabled="true" type="text" class="form-control bg-light" />
                        </div>
                    </div>

                    <!-- Customer Basic Info -->
                    <div class="row g-3 mb-3">
                        <div class="col-md-6">
                            <label class="form-label fw-bold">Nama Calon Customer <span class="text-danger">*</span></label>
                            <input v-model="form.name" type="text" class="form-control" placeholder="Nama lengkap customer" required />
                        </div>
                        <div class="col-md-6">
                            <label class="form-label fw-bold">Nomor WhatsApp / Telp <span class="text-danger">*</span></label>
                            <div class="input-group">
                                <span class="input-group-text"><i class="fa fa-phone"></i></span>
                                <input v-model="form.no_telp" type="text" class="form-control" placeholder="Contoh: 081234567890" required />
                            </div>
                        </div>
                    </div>

                    <!-- Channel / Media & CS Handler -->
                    <div class="row g-3 mb-3">
                        <div class="col-md-6">
                            <label class="form-label fw-bold">Channel / Media Interaksi</label>
                            <select v-model="form.interaction_name" class="form-select">
                                <option value="WhatsApp">WhatsApp Langsung</option>
                                <option value="Facebook Ads">Facebook Ads</option>
                                <option value="Instagram Ads">Instagram Ads</option>
                                <option value="TikTok Ads">TikTok Ads</option>
                                <option value="Google Ads">Google Ads / Website</option>
                                <option value="Telepon">Telepon Masuk</option>
                                <option value="Walk In">Walk In / Kunjungan</option>
                                <option value="Lainnya">Lainnya</option>
                            </select>
                        </div>
                        <div class="col-md-6">
                            <label class="form-label fw-bold">CS / Sales Bertugas</label>
                            <input v-model="form.sales_name" type="text" class="form-control" placeholder="Nama CS yang menangani" />
                        </div>
                    </div>

                    <!-- Inquiry & Notes -->
                    <div class="row g-3 mb-3">
                        <div class="col-md-6">
                            <label class="form-label fw-bold">Keluhan / Kebutuhan Produk</label>
                            <textarea v-model="form.keluhan" class="form-control" rows="2" placeholder="Produk yang ditanyakan atau keluhan calon pembeli..."></textarea>
                        </div>
                        <div class="col-md-6">
                            <label class="form-label fw-bold">Catatan Follow-up / Status</label>
                            <textarea v-model="form.notes" class="form-control" rows="2" placeholder="Catatan respon, alasan reject/closing, dll..."></textarea>
                        </div>
                    </div>

                    <!-- Detailed Address (Accordion for Cleanliness) -->
                    <div class="accordion mb-3" id="addressAccordion">
                        <div class="accordion-item border rounded">
                            <h2 class="accordion-header" id="headingAddress">
                                <button class="accordion-button collapsed py-2" type="button" data-bs-toggle="collapse" data-bs-target="#collapseAddress" aria-expanded="false" aria-controls="collapseAddress">
                                    <i class="fa fa-map-marker text-danger me-2"></i> Data Alamat Pengiriman (Opsional / Jika Sudah Closing)
                                </button>
                            </h2>
                            <div id="collapseAddress" class="accordion-collapse collapse" aria-labelledby="headingAddress" data-bs-parent="#addressAccordion">
                                <div class="accordion-body">
                                    <div class="row g-2 mb-2">
                                        <div class="col-md-6">
                                            <label class="form-label small">Nama Jalan / Alamat</label>
                                            <input v-model="form.street_name" type="text" class="form-control form-control-sm" placeholder="Jl. Mawar No. 10" />
                                        </div>
                                        <div class="col-md-6">
                                            <label class="form-label small">Desa</label>
                                            <input v-model="form.desa" type="text" class="form-control form-control-sm" />
                                        </div>
                                    </div>
                                    <div class="row g-2 mb-2">
                                        <div class="col-md-4">
                                            <label class="form-label small">RT</label>
                                            <input v-model="form.rt" type="text" class="form-control form-control-sm" />
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label small">RW</label>
                                            <input v-model="form.rw" type="text" class="form-control form-control-sm" />
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label small">Kelurahan</label>
                                            <input v-model="form.kelurahan" type="text" class="form-control form-control-sm" />
                                        </div>
                                    </div>
                                    <div class="mb-2">
                                        <label class="form-label small">Patokan</label>
                                        <input v-model="form.patokan" type="text" class="form-control form-control-sm" placeholder="Dekat masjid / seberang ruko..." />
                                    </div>
                                    <div class="row g-2 mb-2">
                                        <div class="col-md-4">
                                            <label class="form-label small">Provinsi</label>
                                            <select v-model="form.province" class="form-select form-select-sm">
                                                <option :value="null">Pilih Provinsi</option>
                                                <option v-for="opt in provinceOptions" :key="opt.province" :value="opt.province">{{ opt.province }}</option>
                                            </select>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label small">Kota / Kabupaten</label>
                                            <select v-model="form.city" class="form-select form-select-sm">
                                                <option :value="null">Pilih Kota</option>
                                                <option v-for="opt in cityOptions" :key="opt.city" :value="opt.city">{{ opt.city }}</option>
                                            </select>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label small">Kecamatan / Area</label>
                                            <select v-model="form.area" class="form-select form-select-sm">
                                                <option :value="null">Pilih Area</option>
                                                <option v-for="opt in areaOptions" :key="opt.area" :value="opt.area">{{ opt.area }}</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div class="row g-2 mb-2">
                                        <div class="col-md-4">
                                            <label class="form-label small">Kode Pos</label>
                                            <input v-model="form.postal_code" type="text" class="form-control form-control-sm" />
                                        </div>
                                        <div class="col-md-8">
                                            <label class="form-label small">Preview Alamat Lengkap</label>
                                            <input :value="customerAddress" type="text" class="form-control form-control-sm bg-light" disabled />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer border-0 bg-light">
                    <button class="btn btn-secondary px-3" type="button" @click="closeModal">Batal</button>
                    <button class="btn btn-primary px-4 shadow-sm" type="button" :disabled="saving" @click="saveInteraction">
                        <span v-if="saving"><i class="fa fa-spinner fa-spin me-1"></i> Menyimpan...</span>
                        <span v-else><i class="fa fa-save me-1"></i> Simpan Data</span>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal WhatsApp Report Preview -->
    <div class="modal fade" id="waReportModal" tabindex="-1" ref="waModalRef" aria-labelledby="waReportModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div class="modal-content shadow-lg border-0">
                <div class="modal-header bg-success text-white border-0 py-3">
                    <h5 class="modal-title fw-bold">
                        <i class="fab fa-whatsapp me-2"></i> Laporan Order Interaction & Performa CS
                    </h5>
                    <button type="button" class="btn-close btn-close-white" @click="closeWaModal" aria-label="Close"></button>
                </div>
                <div class="modal-body p-4">
                    <div class="alert alert-light border d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <span class="fw-bold d-block text-dark">Periode: {{ activePeriodLabel }}</span>
                            <small class="text-muted">Total leads: {{ metrics.total }} | Closing: {{ metrics.closing }} ({{ metrics.closingRate }}%)</small>
                        </div>
                        <button class="btn btn-sm btn-outline-success" @click="copyWhatsAppReport">
                            <i class="fa fa-copy me-1"></i> Salin Teks
                        </button>
                    </div>

                    <div class="mb-3">
                        <label class="form-label fw-bold text-dark small">Preview Format Pesan WhatsApp:</label>
                        <textarea
                            v-model="waReportText"
                            class="form-control font-monospace p-3"
                            rows="14"
                            style="font-size: 13px; line-height: 1.6;"
                            readonly
                        ></textarea>
                    </div>
                </div>
                <div class="modal-footer border-0 bg-light">
                    <button class="btn btn-secondary" type="button" @click="closeWaModal">Tutup</button>
                    <button class="btn btn-success px-3" type="button" @click="copyWhatsAppReport">
                        <i class="fa fa-copy me-1"></i> Salin ke Clipboard
                    </button>
                    <button class="btn btn-primary px-3" type="button" @click="sendWhatsAppDirect">
                        <i class="fab fa-whatsapp me-1"></i> Kirim ke WhatsApp
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal View Detail -->
    <div class="modal fade" id="detailModal" tabindex="-1" ref="detailModalRef" aria-labelledby="detailModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-md modal-dialog-centered" role="document">
            <div class="modal-content border-0 shadow">
                <div class="modal-header bg-light border-0">
                    <h5 class="modal-title fw-bold text-dark">Detail Interaksi Calon Customer</h5>
                    <button type="button" class="btn-close" @click="closeDetailModal" aria-label="Close"></button>
                </div>
                <div class="modal-body p-4" v-if="selectedDetail">
                    <div class="text-center mb-3 pb-3 border-bottom">
                        <div class="avatar-circle mx-auto mb-2 bg-primary-subtle text-primary fw-bold" style="width: 54px; height: 54px; font-size: 22px;">
                            {{ (selectedDetail.name || 'C')[0].toUpperCase() }}
                        </div>
                        <h5 class="fw-bold mb-1 text-dark">{{ selectedDetail.name }}</h5>
                        <span class="badge" :class="getStatusBadgeClass(selectedDetail.status)">
                            <i :class="getStatusIcon(selectedDetail.status)" class="me-1"></i>{{ selectedDetail.status || 'Merespon' }}
                        </span>
                    </div>

                    <div class="row g-2 mb-2">
                        <div class="col-5 text-muted small">Kode Customer</div>
                        <div class="col-7 fw-semibold font-monospace">{{ selectedDetail.code || '-' }}</div>
                    </div>
                    <div class="row g-2 mb-2">
                        <div class="col-5 text-muted small">No. WhatsApp / Telp</div>
                        <div class="col-7 fw-semibold">
                            {{ selectedDetail.no_telp || '-' }}
                            <a v-if="selectedDetail.no_telp" :href="getWaChatUrl(selectedDetail.no_telp, selectedDetail.name)" target="_blank" class="ms-1 text-success">
                                <i class="fab fa-whatsapp"></i> Chat
                            </a>
                        </div>
                    </div>
                    <div class="row g-2 mb-2">
                        <div class="col-5 text-muted small">Tanggal Masuk</div>
                        <div class="col-7 fw-semibold">{{ formatDateTime(selectedDetail.interaction_date || selectedDetail.CreatedAt) }}</div>
                    </div>
                    <div class="row g-2 mb-2">
                        <div class="col-5 text-muted small">CS Bertugas</div>
                        <div class="col-7 fw-semibold">{{ selectedDetail.sales_name || selectedDetail.user_email || '-' }}</div>
                    </div>
                    <div class="row g-2 mb-2">
                        <div class="col-5 text-muted small">Divisi / Tim</div>
                        <div class="col-7 fw-semibold">{{ selectedDetail.divisi?.name || '-' }} / {{ selectedDetail.divisi_team?.name || '-' }}</div>
                    </div>
                    <div class="row g-2 mb-2" v-if="selectedDetail.interaction_name">
                        <div class="col-5 text-muted small">Channel Media</div>
                        <div class="col-7 fw-semibold">{{ selectedDetail.interaction_name }}</div>
                    </div>
                    <div class="row g-2 mb-2">
                        <div class="col-5 text-muted small">Keluhan / Kebutuhan</div>
                        <div class="col-7">{{ selectedDetail.keluhan || '-' }}</div>
                    </div>
                    <div class="row g-2 mb-2">
                        <div class="col-5 text-muted small">Catatan Follow-up</div>
                        <div class="col-7">{{ selectedDetail.notes || '-' }}</div>
                    </div>
                    <div class="row g-2 mb-2" v-if="selectedDetail.address || selectedDetail.city">
                        <div class="col-5 text-muted small">Alamat Lengkap</div>
                        <div class="col-7">{{ selectedDetail.address || [selectedDetail.city, selectedDetail.province].filter(Boolean).join(', ') }}</div>
                    </div>
                </div>
                <div class="modal-footer border-0 bg-light">
                    <button class="btn btn-secondary btn-sm" @click="closeDetailModal">Tutup</button>
                    <button v-if="selectedDetail?.status === 'Closing'" class="btn btn-success btn-sm" @click="goToPOS(selectedDetail)">
                        <i class="fa fa-shopping-cart me-1"></i> Buka POS Order
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, computed, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'
import * as master from "@/api/services/MasterRequest"
import { useSessionStore } from '@/store/session'
import * as XLSX from 'xlsx'

const router = useRouter()
const session = useSessionStore()

// Interfaces
interface Customer {
    ID: any;
    code: string;
    name: string;
    no_telp: string;
    status: string;
    interaction_date?: string;
    interaction_name?: string;
    sales_name?: string;
    notes?: string;
    user_email?: string;
    divisi?: any;
    divisi_team?: any;
    keluhan?: string;
    address?: string;
    street_name?: string;
    desa?: string;
    rt?: string;
    rw?: string;
    kelurahan?: string;
    patokan?: string;
    province?: string;
    city?: string;
    area?: string;
    postal_code?: string;
    CreatedAt?: string;
    UpdatedAt?: string;
}

interface DataTableParams {
    page: number;
    size: number;
    search: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    user_email?: string;
    divisi_id?: number | null;
    divisi_team_id?: number | null;
    no_group?: string;
}

// Table configuration
const headers = [
    { text: 'Actions', value: 'actions' },
    { text: 'Status', value: 'status' },
    { text: 'Customer', value: 'name' },
    { text: 'No. WhatsApp', value: 'no_telp' },
    { text: 'Tgl Interaksi', value: 'interaction_date' },
    { text: 'CS / Sales', value: 'sales_name' },
    { text: 'Divisi / Tim', value: 'divisi' },
    { text: 'Keluhan/Kebutuhan', value: 'keluhan' },
    { text: 'Catatan Follow-up', value: 'notes' }
]

const customers = ref<Customer[]>([])
const loading = ref(false)
const saving = ref(false)
const downloadingExcel = ref(false)
const total = ref(0)
const showSalesRank = ref(true)

// Period & Filters state
const activePeriod = ref<'today' | 'yesterday' | 'week' | 'month' | 'all'>('today')
const filterStartDate = ref('')
const filterEndDate = ref('')
const filterStatus = ref('')
const filterDivisi = ref<number | null>(null)
const filterDivisiTeam = ref<number | null>(null)

const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: '',
    status: '',
    start_date: '',
    end_date: '',
    no_group: 'true'
})

// Metrics for the active period
const metrics = reactive({
    total: 0,
    merespon: 0,
    tidakMerespon: 0,
    closing: 0,
    reject: 0,
    cancel: 0,
    responseRate: 0,
    noResponseRate: 0,
    closingRate: 0,
    dropRate: 0
})

// Sales / CS performance breakdown
interface SalesPerformance {
    salesName: string;
    salesEmail: string;
    total: number;
    merespon: number;
    tidakMerespon: number;
    closing: number;
    reject: number;
    cancel: number;
    closingRate: number;
    responseRate: number;
}
const salesPerformanceList = ref<SalesPerformance[]>([])

// Options
const modalTitle = ref('')
const divisiOptions = ref<{ ID: any, name: string, divisiTeams?: any[] }[]>([])
const divisiTeamOptions = ref<{ ID: any, name: string }[]>([])
const filterDivisiTeamOptions = ref<{ ID: any, name: string }[]>([])
const provinceOptions = ref<{ province: string }[]>([])
const cityOptions = ref<{ city: string }[]>([])
const areaOptions = ref<{ area: string }[]>([])

// Modals
const modalRef = ref<HTMLElement | null>(null)
let bsModal: any = null

const waModalRef = ref<HTMLElement | null>(null)
let bsWaModal: any = null

const detailModalRef = ref<HTMLElement | null>(null)
let bsDetailModal: any = null

const selectedDetail = ref<Customer | null>(null)
const waReportText = ref('')

// Form state
const form = reactive<any>({
    ID: null,
    code: '',
    name: '',
    status: 'Merespon',
    interaction_date_str: '',
    interaction_name: 'WhatsApp',
    sales_name: '',
    user_email: '',
    notes: '',
    divisi: null,
    divisi_team: null,
    no_telp: '',
    keluhan: '',
    street_name: '',
    desa: '',
    rt: '',
    rw: '',
    kelurahan: '',
    patokan: '',
    province: null,
    city: null,
    area: null,
    postal_code: '',
    address: ''
})

const isEditing = ref(false)

// Active Period Label
const activePeriodLabel = computed(() => {
    if (activePeriod.value === 'today') return 'Hari Ini'
    if (activePeriod.value === 'yesterday') return 'Kemarin'
    if (activePeriod.value === 'week') return '7 Hari Terakhir'
    if (activePeriod.value === 'month') return 'Bulan Ini'
    if (filterStartDate.value && filterEndDate.value) {
        return `${filterStartDate.value} s/d ${filterEndDate.value}`
    }
    return 'Semua Data'
})

// Quick Period Preset Selection
const setPeriodPreset = (preset: 'today' | 'yesterday' | 'week' | 'month' | 'all') => {
    activePeriod.value = preset
    const now = new Date()

    if (preset === 'today') {
        const todayStr = toDateInputFormat(now)
        filterStartDate.value = todayStr
        filterEndDate.value = todayStr
    } else if (preset === 'yesterday') {
        const yest = new Date()
        yest.setDate(yest.getDate() - 1)
        const yestStr = toDateInputFormat(yest)
        filterStartDate.value = yestStr
        filterEndDate.value = yestStr
    } else if (preset === 'week') {
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 6)
        filterStartDate.value = toDateInputFormat(weekAgo)
        filterEndDate.value = toDateInputFormat(now)
    } else if (preset === 'month') {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
        filterStartDate.value = toDateInputFormat(firstDay)
        filterEndDate.value = toDateInputFormat(now)
    } else {
        filterStartDate.value = ''
        filterEndDate.value = ''
    }

    applyFilter()
}

const toDateInputFormat = (d: Date): string => {
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const applyDateFilter = () => {
    activePeriod.value = 'all'
    applyFilter()
}

const applyStatusFilter = () => {
    applyFilter()
}

const handleFilterDivisiChange = () => {
    if (filterDivisi.value) {
        const found = divisiOptions.value.find(d => d.ID === filterDivisi.value)
        filterDivisiTeamOptions.value = found?.divisiTeams || []
    } else {
        filterDivisiTeamOptions.value = []
    }
    filterDivisiTeam.value = null
    applyFilter()
}

const applyFilter = () => {
    params.page = 1
    params.start_date = filterStartDate.value || ''
    params.end_date = filterEndDate.value || ''
    params.status = filterStatus.value || ''
    params.divisi_id = filterDivisi.value || null
    params.divisi_team_id = filterDivisiTeam.value || null
    fetchCustomers()
}

const resetFilters = () => {
    filterStatus.value = ''
    filterDivisi.value = null
    filterDivisiTeam.value = null
    filterDivisiTeamOptions.value = []
    setPeriodPreset('today')
}

// Fetch Customers & Calculate Metrics
const fetchCustomers = async () => {
    loading.value = true
    try {
        const user = session.getUser()
        if (user && user.role === 'SALES') {
            params.user_email = user.email
        }

        const resp = await master.postRequest("/customer/list", { ...params, no_group: 'true' })
        customers.value = resp.data || []
        total.value = resp.total || 0

        // Calculate metrics and CS breakdown
        calculateMetrics(customers.value)
    } catch (error) {
        toast.error('Gagal mengambil data interaksi order')
    } finally {
        loading.value = false
    }
}

// Compute performance metrics
const calculateMetrics = (items: Customer[]) => {
    let t = items.length
    let m = 0
    let tm = 0
    let c = 0
    let r = 0
    let can = 0

    const salesMap = new Map<string, SalesPerformance>()

    items.forEach((item) => {
        const s = item.status || 'Merespon'
        if (s === 'Merespon') m++
        else if (s === 'Tidak Merespon') tm++
        else if (s === 'Closing') c++
        else if (s === 'Reject') r++
        else if (s === 'Cancel') can++

        const sName = item.sales_name || item.user_email || 'Unassigned'
        const sEmail = item.user_email || ''

        if (!salesMap.has(sName)) {
            salesMap.set(sName, {
                salesName: sName,
                salesEmail: sEmail,
                total: 0,
                merespon: 0,
                tidakMerespon: 0,
                closing: 0,
                reject: 0,
                cancel: 0,
                closingRate: 0,
                responseRate: 0
            })
        }

        const curr = salesMap.get(sName)!
        curr.total++
        if (s === 'Merespon') curr.merespon++
        else if (s === 'Tidak Merespon') curr.tidakMerespon++
        else if (s === 'Closing') curr.closing++
        else if (s === 'Reject') curr.reject++
        else if (s === 'Cancel') curr.cancel++
    })

    metrics.total = t
    metrics.merespon = m
    metrics.tidakMerespon = tm
    metrics.closing = c
    metrics.reject = r
    metrics.cancel = can

    metrics.responseRate = t > 0 ? Math.round(((m + c) / t) * 100) : 0
    metrics.noResponseRate = t > 0 ? Math.round((tm / t) * 100) : 0
    metrics.closingRate = t > 0 ? Math.round((c / t) * 100) : 0
    metrics.dropRate = t > 0 ? Math.round(((r + can) / t) * 100) : 0

    // Compute sales rank
    const arr: SalesPerformance[] = []
    salesMap.forEach((val) => {
        val.closingRate = val.total > 0 ? Math.round((val.closing / val.total) * 100) : 0
        val.responseRate = val.total > 0 ? Math.round(((val.merespon + val.closing) / val.total) * 100) : 0
        arr.push(val)
    })

    // Sort by closing count descending, then total descending
    arr.sort((a, b) => b.closing - a.closing || b.total - a.total)
    salesPerformanceList.value = arr
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchCustomers()
}

// Quick Status Switcher
const quickChangeStatus = async (item: Customer, newStatus: string) => {
    const oldStatus = item.status
    item.status = newStatus // Optimistic update

    try {
        // Try dedicated update-status or fallback to standard update
        let updated = false
        try {
            const resp = await master.postRequest("/customer/update-status", {
                ID: item.ID,
                status: newStatus,
                notes: item.notes || ''
            })
            if (resp && resp.status === 200) updated = true
        } catch {
            // fallback
            const resp = await master.postRequest("/customer/update", {
                ...item,
                status: newStatus
            })
            if (resp && resp.status === 200) updated = true
        }

        toast.success(`Status berhasil diubah ke "${newStatus}"`)
        fetchCustomers()
    } catch (err: any) {
        item.status = oldStatus // Revert
        toast.error('Gagal memperbarui status')
    }
}

// Master Data Fetchers
const fetchDivisi = async () => {
    try {
        const resp = await master.postRequest("/divisi/list", { page: 1, size: 1000, search: '' })
        divisiOptions.value = resp.data || []
    } catch (error) {
        console.error('Failed to fetch divisi', error)
    }
}

const fetchProvince = async () => {
    try {
        const resp = await master.postRequest("/destination/province", { page: 1, size: 1000, search: '' })
        provinceOptions.value = resp.data || []
    } catch (error) {
        console.error('Failed to fetch province', error)
    }
}

const fetchCity = async (province: string) => {
    try {
        const resp = await master.postRequest("/destination/city", { page: 1, size: 1000, search: province })
        cityOptions.value = resp.data || []
    } catch (error) {
        console.error('Failed to fetch cities', error)
    }
}

const fetchArea = async (city: string) => {
    try {
        const resp = await master.postRequest("/destination/area", { page: 1, size: 1000, search: city })
        areaOptions.value = resp.data || []
    } catch (error) {
        console.error('Failed to fetch area', error)
    }
}

// Modal open/close & CRUD
const openModal = (customer: Customer | null = null) => {
    const currentUser = session.getUser()
    if (customer) {
        modalTitle.value = 'Edit Interaksi Customer'
        isEditing.value = true
        Object.assign(form, customer)

        if (customer.interaction_date) {
            form.interaction_date_str = new Date(customer.interaction_date).toISOString().slice(0, 16)
        } else {
            form.interaction_date_str = new Date().toISOString().slice(0, 16)
        }

        nextTick(() => {
            if (customer.divisi && divisiOptions.value.length > 0) {
                form.divisi = divisiOptions.value.find(opt => opt.ID === (customer.divisi.ID || customer.divisi)) || null
                divisiTeamOptions.value = form.divisi?.divisiTeams || []
            }
            if (customer.divisi_team && divisiTeamOptions.value.length > 0) {
                form.divisi_team = divisiTeamOptions.value.find(opt => opt.ID === (customer.divisi_team.ID || customer.divisi_team)) || null
            }
            if (customer.province) {
                form.province = customer.province
                fetchCity(customer.province)
                setTimeout(() => {
                    if (customer.city) {
                        form.city = customer.city
                        fetchArea(customer.city)
                        setTimeout(() => {
                            if (customer.area) form.area = customer.area
                            isEditing.value = false
                        }, 300)
                    }
                }, 300)
            } else {
                isEditing.value = false
            }
        })
    } else {
        modalTitle.value = 'Input Interaksi Masuk'
        Object.keys(form).forEach(k => form[k] = '')
        form.ID = null
        form.status = 'Merespon'
        form.interaction_name = 'WhatsApp'
        form.interaction_date_str = new Date().toISOString().slice(0, 16)
        form.sales_name = currentUser?.nick_name || currentUser?.name || currentUser?.email || ''
        form.user_email = currentUser?.email || ''
        form.divisi = divisiOptions.value.length > 0 ? divisiOptions.value[0] : null
        divisiTeamOptions.value = form.divisi?.divisiTeams || []
        form.divisi_team = null
    }

    if (bsModal) bsModal.show()
}

const closeModal = () => {
    if (bsModal) bsModal.hide()
}

const saveInteraction = async () => {
    if (!form.name || !form.no_telp) {
        toast.warning('Nama Customer dan No. Telp wajib diisi!')
        return
    }

    saving.value = true
    form.address = customerAddress.value

    try {
        const payload: any = { ...form }
        if (form.interaction_date_str) {
            payload.interaction_date = new Date(form.interaction_date_str).toISOString()
        }

        if (form.ID) {
            const resp = await master.postRequest("/customer/update", payload)
            if (resp && resp.status === 200) {
                toast.success('Interaksi customer berhasil diperbarui!')
                closeModal()
                fetchCustomers()
            } else {
                toast.error(resp?.message || 'Gagal memperbarui interaksi')
            }
        } else {
            const resp = await master.postRequest("/customer/create", payload)
            if (resp && resp.status === 200) {
                toast.success('Interaksi baru berhasil disimpan!')
                closeModal()
                fetchCustomers()
            } else {
                toast.error(resp?.message || 'Gagal menyimpan interaksi')
            }
        }
    } catch (err: any) {
        toast.error(err.message || 'Terjadi kesalahan saat menyimpan')
    } finally {
        saving.value = false
    }
}

const editCustomer = async (item: Customer) => {
    loading.value = true
    try {
        const resp = await master.postRequest("/customer/data", { ID: item.ID })
        if (resp && resp.status === 200) {
            openModal(resp.data)
        } else {
            openModal(item)
        }
    } catch {
        openModal(item)
    } finally {
        loading.value = false
    }
}

const deleteCustomer = async (item: Customer) => {
    if (!confirm(`Hapus data interaksi customer "${item.name}"?`)) return
    loading.value = true
    try {
        const resp = await master.postRequest("/customer/delete", { ID: item.ID })
        if (resp && resp.status === 200) {
            toast.success('Data interaksi berhasil dihapus')
            fetchCustomers()
        } else {
            toast.error('Gagal menghapus data')
        }
    } catch (err: any) {
        toast.error(err.message || 'Gagal menghapus data')
    } finally {
        loading.value = false
    }
}

// Detail View Modal
const viewDetail = (item: Customer) => {
    selectedDetail.value = item
    if (bsDetailModal) bsDetailModal.show()
}

const closeDetailModal = () => {
    if (bsDetailModal) bsDetailModal.hide()
}

// Navigation to POS on Closing
const goToPOS = (item: Customer) => {
    closeDetailModal()
    router.push({
        path: '/sales/pos',
        query: {
            customer_id: item.ID,
            customer_name: item.name,
            customer_phone: item.no_telp
        }
    })
}

// WhatsApp Report Generation
const generateWhatsAppReportText = (): string => {
    const user = session.getUser()
    const reporterName = user?.nick_name || user?.name || user?.email || 'Admin/CS'
    const nowStr = new Date().toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    }) + ' ' + new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })

    let text = `📊 *LAPORAN ORDER INTERACTION & PERFORMA SALES/CS*\n`
    text += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n`
    text += `📅 *Periode:* ${activePeriodLabel.value}\n`
    if (filterDivisi.value) {
        const dObj = divisiOptions.value.find(d => d.ID === filterDivisi.value)
        text += `🏢 *Divisi:* ${dObj?.name || 'Semua Divisi'}\n`
    }
    text += `⏰ *Waktu Laporan:* ${nowStr} WIB\n\n`

    text += `📈 *RINGKASAN STATUS INTERAKSI:*\n`
    text += `• Total Interaksi Masuk : *${metrics.total}* Leads\n`
    text += `• Merespon              : *${metrics.merespon}* (${metrics.responseRate}%)\n`
    text += `• Tidak Merespon        : *${metrics.tidakMerespon}* (${metrics.noResponseRate}%)\n`
    text += `• Closing (Order)       : *${metrics.closing}* (${metrics.closingRate}%) 🎯\n`
    text += `• Reject                : *${metrics.reject}*\n`
    text += `• Cancel                : *${metrics.cancel}*\n\n`

    text += `🎯 *CLOSING RATE:* *${metrics.closingRate}%*\n`
    text += `⚡ *RESPONSE RATE:* *${metrics.responseRate}%*\n`
    text += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`

    text += `👥 *KEMAMPUAN / PERFORMA CS PER-AGENT:*\n`
    if (salesPerformanceList.value.length === 0) {
        text += `(Belum ada data CS pada periode ini)\n`
    } else {
        salesPerformanceList.value.forEach((cs, i) => {
            text += `${i + 1}. *${cs.salesName}*\n`
            text += `   - Total Handle : *${cs.total}* Leads\n`
            text += `   - Closing      : *${cs.closing}* (${cs.closingRate}%) 🎯\n`
            text += `   - Respon       : *${cs.merespon}* | Tdk Respon: *${cs.tidakMerespon}*\n`
            text += `   - Reject/Cancel: *${cs.reject + cs.cancel}*\n`
        })
    }
    text += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n`

    // Recent Closing Leads
    const closingItems = customers.value.filter(c => c.status === 'Closing').slice(0, 5)
    if (closingItems.length > 0) {
        text += `🎯 *DAFTAR CLOSING TERBARU:*\n`
        closingItems.forEach((ci) => {
            text += `• ${ci.name} (${ci.no_telp || '-'}) - CS: ${ci.sales_name || ci.user_email || '-'}\n`
        })
        text += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n`
    }

    text += `👤 *Dilaporkan oleh:* ${reporterName}\n`
    text += `_Sistem Order Interaction - INDOFORCETECH_`

    return text
}

const openWhatsAppModal = () => {
    waReportText.value = generateWhatsAppReportText()
    if (bsWaModal) bsWaModal.show()
}

const closeWaModal = () => {
    if (bsWaModal) bsWaModal.hide()
}

const copyWhatsAppReport = () => {
    const text = waReportText.value
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Format WhatsApp berhasil disalin ke clipboard! 📋')
        }).catch(() => {
            fallbackCopy(text)
        })
    } else {
        fallbackCopy(text)
    }
}

const fallbackCopy = (text: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
        document.execCommand('copy')
        toast.success('Format WhatsApp berhasil disalin ke clipboard! 📋')
    } catch {
        toast.error('Gagal menyalin teks')
    }
    document.body.removeChild(textArea)
}

const sendWhatsAppDirect = () => {
    const text = encodeURIComponent(waReportText.value)
    window.open(`https://wa.me/?text=${text}`, '_blank')
}

// Download Excel Report
const downloadExcelReport = async () => {
    downloadingExcel.value = true
    try {
        // Fetch all records for the period (up to 10000)
        const fetchParams = {
            ...params,
            page: 1,
            size: 10000,
            no_group: 'true'
        }
        const resp = await master.postRequest("/customer/list", fetchParams)
        const allItems: Customer[] = resp.data || customers.value

        // Sheet 1: Data Interaksi
        const interactionRows = allItems.map((item, index) => ({
            'No': index + 1,
            'Tanggal': formatDate(item.interaction_date || item.CreatedAt),
            'Jam': formatTime(item.interaction_date || item.CreatedAt),
            'Kode Customer': item.code || '-',
            'Nama Customer': item.name,
            'No. WhatsApp': item.no_telp || '-',
            'Status Interaksi': item.status || 'Merespon',
            'Channel / Media': item.interaction_name || '-',
            'Sales / CS': item.sales_name || item.user_email || '-',
            'Divisi': item.divisi?.name || '-',
            'Tim Divisi': item.divisi_team?.name || '-',
            'Keluhan / Kebutuhan': item.keluhan || '-',
            'Catatan Follow-up': item.notes || '-',
            'Kota': item.city || '-',
            'Provinsi': item.province || '-',
            'Alamat Lengkap': item.address || '-'
        }))

        // Sheet 2: Performa CS
        const performanceRows = salesPerformanceList.value.map((cs, index) => ({
            'Rank': index + 1,
            'Nama Sales / CS': cs.salesName,
            'Email': cs.salesEmail,
            'Total Leads Masuk': cs.total,
            'Merespon': cs.merespon,
            'Tidak Merespon': cs.tidakMerespon,
            'Closing (Order)': cs.closing,
            'Reject': cs.reject,
            'Cancel': cs.cancel,
            'Response Rate (%)': `${cs.responseRate}%`,
            'Closing Rate (%)': `${cs.closingRate}%`
        }))

        // Create workbook
        const wb = XLSX.utils.book_new()
        const wsInteractions = XLSX.utils.json_to_sheet(interactionRows)
        const wsPerformance = XLSX.utils.json_to_sheet(performanceRows)

        XLSX.utils.book_append_sheet(wb, wsInteractions, 'Data Interaksi')
        XLSX.utils.book_append_sheet(wb, wsPerformance, 'Performa Sales CS')

        const startStr = filterStartDate.value || 'All'
        const endStr = filterEndDate.value || 'All'
        const filename = `Laporan_Order_Interaction_${startStr}_sd_${endStr}.xlsx`

        XLSX.writeFile(wb, filename)
        toast.success('Laporan Excel berhasil diunduh! 📊')
    } catch (err: any) {
        toast.error('Gagal mengunduh file Excel')
    } finally {
        downloadingExcel.value = false
    }
}

// Helpers
const getStatusBadgeClass = (status?: string): string => {
    switch (status) {
        case 'Closing':
            return 'btn-success text-white'
        case 'Merespon':
            return 'btn-info text-white'
        case 'Tidak Merespon':
            return 'btn-warning text-dark'
        case 'Reject':
            return 'btn-danger text-white'
        case 'Cancel':
            return 'btn-secondary text-white'
        default:
            return 'btn-info text-white'
    }
}

const getStatusIcon = (status?: string): string => {
    switch (status) {
        case 'Closing':
            return 'fa fa-check-circle'
        case 'Merespon':
            return 'fa fa-commenting'
        case 'Tidak Merespon':
            return 'fa fa-hourglass-half'
        case 'Reject':
            return 'fa fa-times-circle'
        case 'Cancel':
            return 'fa fa-ban'
        default:
            return 'fa fa-commenting'
    }
}

const getWaChatUrl = (phone: string, customerName: string): string => {
    let cleanPhone = phone.replace(/[^0-9]/g, '')
    if (cleanPhone.startsWith('0')) {
        cleanPhone = '62' + cleanPhone.substring(1)
    }
    const msg = encodeURIComponent(`Halo Kak ${customerName}, perkenalkan saya dari Customer Service INDOFORCETECH. Ada yang bisa kami bantu?`)
    return `https://wa.me/${cleanPhone}?text=${msg}`
}

const formatDate = (dateStr?: string | Date) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return '-'
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const formatTime = (dateStr?: string | Date) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) + ' WIB'
}

const formatDateTime = (dateStr?: string | Date) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return '-'
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
        d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) + ' WIB'
}

const customerAddress = computed(() => {
    const parts = [
        form.street_name || '',
        form.desa ? 'Desa ' + form.desa : '',
        form.rt ? `RT ${form.rt}` : '',
        form.rw ? `RW ${form.rw}` : '',
        form.kelurahan ? `Kel. ${form.kelurahan}` : '',
        form.patokan ? `(${form.patokan})` : '',
        form.area || '',
        form.city || '',
        form.province || '',
        form.postal_code || ''
    ]
    return parts.filter(Boolean).join(', ')
})

// Watchers
watch(() => form.divisi, (newDivisi) => {
    if (isEditing.value) return
    if (newDivisi && newDivisi.divisiTeams) {
        divisiTeamOptions.value = newDivisi.divisiTeams
        form.divisi_team = null
    } else {
        divisiTeamOptions.value = []
        form.divisi_team = null
    }
})

watch(() => form.province, (newProvince) => {
    if (isEditing.value) return
    if (newProvince) {
        fetchCity(newProvince)
        form.city = null
        areaOptions.value = []
        form.area = null
    } else {
        cityOptions.value = []
        form.city = null
        areaOptions.value = []
        form.area = null
    }
})

watch(() => form.city, (newCity) => {
    if (isEditing.value) return
    if (newCity) {
        fetchArea(newCity)
        form.area = null
    } else {
        areaOptions.value = []
        form.area = null
    }
})

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    if (waModalRef.value) {
        bsWaModal = new Modal(waModalRef.value)
    }
    if (detailModalRef.value) {
        bsDetailModal = new Modal(detailModalRef.value)
    }

    fetchDivisi()
    fetchProvince()
    setPeriodPreset('today')
})
</script>

<style scoped>
.stat-card {
    border-radius: 12px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08) !important;
}
.stat-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.avatar-circle {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}
.cursor-pointer {
    cursor: pointer;
}
</style>
